// src/components/Inquiry/hooks/useInquiryItems.ts
import { useEffect, useMemo, useReducer, useRef } from "react";
import { CartItemType } from "types/CartTypes";
import { OrderItemType } from "types/OrderTypes";
import { CreateInquiryItemType, InquiryTypeEnum } from "types/InquiryTypes";
import { CartType } from "types/CartTypes";
import { OrderType } from "types/OrderTypes";
import { filterItemsByType } from "../utils/filterItemsByType";

// ─── Types ───────────────────────────────────────────────────────────────────

export type SelectedItem = CreateInquiryItemType & { id?: number };

type ItemsState = {
  prodItems: (OrderItemType | CartItemType)[];
  selectedItems: SelectedItem[];
  customItems: (OrderItemType | CartItemType)[];
};

type ItemsAction =
  | {
      type: "RESET";
      prodItems: (OrderItemType | CartItemType)[];
      selectedItems: SelectedItem[];
    }
  // Update mode only: the type filter changed, so re-list the picker and keep
  // whichever selections are still eligible. RESET would auto-select-all over
  // the top of an existing inquiry's lines.
  | { type: "REFILTER"; prodItems: (OrderItemType | CartItemType)[] }
  | { type: "TOGGLE_ITEM"; item: SelectedItem }
  | { type: "SELECT_ALL" }
  | { type: "DESELECT_ALL" }
  | { type: "ADD_CUSTOM"; item: OrderItemType | CartItemType }
  | { type: "UPDATE_CUSTOM"; item: OrderItemType | CartItemType }
  | { type: "REMOVE_CUSTOM"; itemId: number };

// ─── Reducer ─────────────────────────────────────────────────────────────────

function itemsReducer(state: ItemsState, action: ItemsAction): ItemsState {
  switch (action.type) {
    case "RESET":
      return {
        ...state,
        prodItems: action.prodItems,
        selectedItems: action.selectedItems,
      };

    case "REFILTER": {
      const eligible = new Set(action.prodItems.map((item) => item.product.id));
      return {
        ...state,
        prodItems: action.prodItems,
        selectedItems: state.selectedItems.filter((s) =>
          eligible.has(s.productId)
        ),
      };
    }

    case "TOGGLE_ITEM": {
      const exists = state.selectedItems.some(
        (s) => s.productId === action.item.productId
      );
      return {
        ...state,
        selectedItems: exists
          ? state.selectedItems.filter(
              (s) => s.productId !== action.item.productId
            )
          : [...state.selectedItems, action.item],
      };
    }

    case "SELECT_ALL":
      return { ...state, selectedItems: state.prodItems.map(prodItemToSelected) };

    case "DESELECT_ALL":
      return { ...state, selectedItems: [] };

    case "ADD_CUSTOM":
      return { ...state, customItems: [...state.customItems, action.item] };

    case "UPDATE_CUSTOM":
      return {
        ...state,
        customItems: state.customItems.map((c) =>
          c.id === action.item.id ? action.item : c
        ),
      };

    case "REMOVE_CUSTOM":
      return {
        ...state,
        customItems: state.customItems.filter((c) => c.id !== action.itemId),
      };

    default:
      return state;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function prodItemToSelected(item: OrderItemType | CartItemType): SelectedItem {
  const selected: SelectedItem = {
    productId: item.product.id,
    quantity: item.quantity,
  };
  // Attach orderItemId or quoteItemId if present on the item
  if ((item as any).orderId !== undefined) {
    selected.orderItemId = item.id;
  } else if ((item as any).quoteId !== undefined) {
    selected.quoteItemId = item.id;
  }
  return selected;
}

/**
 * A free-text line in the shape the inquiry payload wants it. Exported because
 * InquiryEditForm builds the same list to compare against, and the two drifting
 * apart makes an untouched inquiry read as dirty.
 *
 * No price: a line is priced when the purchase order is placed (EE-253), and
 * update_inquiry keeps the saved price of a line sent without one.
 */
export const customItemToInquiryItem = (
  item: OrderItemType | CartItemType
): CreateInquiryItemType => ({
  productId: item.product.id,
  orderItemId: null,
  quoteItemId: null,
  quantity: item.quantity,
});

function autoSelect(
  items: (OrderItemType | CartItemType)[],
  prodType: "order" | "cart"
): SelectedItem[] {
  return items.map((item) => {
    const selected: SelectedItem = {
      productId: item.product.id,
      quantity: item.quantity,
    };
    if (prodType === "order") selected.orderItemId = item.id;
    else selected.quoteItemId = item.id;
    return selected;
  });
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export type UseInquiryItemsResult = {
  prodItems: (OrderItemType | CartItemType)[];
  selectedItems: SelectedItem[];
  customItems: (OrderItemType | CartItemType)[];
  mergedItems: CreateInquiryItemType[];
  toggle: (item: SelectedItem) => void;
  selectAll: () => void;
  deselectAll: () => void;
  addCustomItem: (item: OrderItemType | CartItemType) => void;
  updateCustomItem: (item: OrderItemType | CartItemType) => void;
  removeCustomItem: (itemId: number) => void;
};

/**
 * Manages item selection state for the inquiry form.
 *
 * - Watches `prodObj` and `type`; re-filters and resets selectedItems automatically.
 * - Pass `initialSelected` (from an existing inquiry) to seed selection on first
 *   load instead of auto-selecting all filtered items.
 */
export function useInquiryItems(
  prodObj: OrderType | CartType | undefined,
  type: InquiryTypeEnum,
  prodType: "order" | "cart",
  initialSelected?: SelectedItem[],
  initialCustomItems?: (OrderItemType | CartItemType)[]
): UseInquiryItemsResult {
  const [state, dispatch] = useReducer(itemsReducer, {
    prodItems: [],
    selectedItems: [],
    customItems: initialCustomItems ?? [],
  });

  // Track whether we've done the initial seed from an old inquiry.
  const hasSeeded = useRef(false);
  // Which mode this hook is in, fixed at mount: an inquiry does not stop being
  // an existing one part-way through editing it.
  const isUpdate = useRef(initialSelected !== undefined);

  useEffect(() => {
    // Un-picking the order has to clear what was picked from it.
    // InquiryOrderPicker calls onPick(undefined) on every keystroke, and this
    // used to `return` here - leaving the old order's lines selected, so the
    // payload carried orderItemIds belonging to an order it no longer names.
    if (!prodObj) {
      dispatch({ type: "RESET", prodItems: [], selectedItems: [] });
      return;
    }

    const filtered = filterItemsByType(prodObj, type);

    // First pass. In update mode the inquiry's own lines are the selection;
    // otherwise a fresh inquiry starts with everything the order offers.
    if (!hasSeeded.current) {
      hasSeeded.current = true;
      dispatch({
        type: "RESET",
        prodItems: filtered,
        selectedItems: isUpdate.current
          ? initialSelected!
          : autoSelect(filtered, prodType),
      });
      return;
    }

    // Later passes are a type change. Selecting everything again is right for
    // a new inquiry - the user just said what kind of work this is. It is not
    // right for one that already exists: it threw away the lines the inquiry
    // was saved with and replaced them with every line of the new type, with
    // nothing on screen to say it had happened.
    if (isUpdate.current) dispatch({ type: "REFILTER", prodItems: filtered });
    else
      dispatch({
        type: "RESET",
        prodItems: filtered,
        selectedItems: autoSelect(filtered, prodType),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, prodObj]);

  const mergedItems = useMemo<CreateInquiryItemType[]>(
    () => [...state.selectedItems, ...state.customItems.map(customItemToInquiryItem)],
    [state.selectedItems, state.customItems]
  );

  return {
    prodItems: state.prodItems,
    selectedItems: state.selectedItems,
    customItems: state.customItems,
    mergedItems,
    toggle: (item) => dispatch({ type: "TOGGLE_ITEM", item }),
    selectAll: () => dispatch({ type: "SELECT_ALL" }),
    deselectAll: () => dispatch({ type: "DESELECT_ALL" }),
    addCustomItem: (item) => dispatch({ type: "ADD_CUSTOM", item }),
    updateCustomItem: (item) => dispatch({ type: "UPDATE_CUSTOM", item }),
    removeCustomItem: (itemId) => dispatch({ type: "REMOVE_CUSTOM", itemId }),
  };
}
